import { Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowsRotate,
  faChevronDown,
  faChevronRight,
  faGears,
  faTriangleExclamation,
} from '@fortawesome/free-solid-svg-icons';
import { SkeletonTable } from '@/components/skeletons';
import { getApiErrorMessage } from '@/lib/api-error';
import { canPlatform } from '@/lib/platform-permissions';
import { usePlatformAuthStore } from '@/stores/platform-auth.store';
import {
  fetchFailedJobs,
  fetchJobQueues,
  retryJob,
  type PlatformFailedJob,
  type PlatformJobQueueList,
} from './api/platform-operations.api';

/**
 * Phase 12.7b Wave 1 — Platform Console → Operations → Background Jobs.
 *
 * Governance: **W1-Q2 = A (BullMQ rolling window)** · **W1-Q3 = B (retry included)** ·
 * **OD-6 CLOSED** (no tenant business payloads).
 *
 * ── WHAT THIS PAGE IS NOT ──────────────────────────────────────────────────
 * Not job history. BullMQ retains a rolling window per queue and nothing else is
 * stored — no table, no snapshot, no migration. The page states that limit
 * rather than letting a list of jobs imply a durable trail.
 *
 * Not a payload viewer. `job.data` is never returned by the API and never
 * rendered here: five of the ten queues carry tenant-plane work, and the
 * *Principle Reminder* forbids tenant business data in the Platform Console.
 */

const PAGE_SIZES = [20, 50, 100] as const;
type LoadState = 'loading' | 'ready' | 'error';

export function BackgroundJobsPage() {
  const role = usePlatformAuthStore((s) => s.user?.role);
  // UX only, and it fails closed. The backend refuses regardless of what is drawn.
  const canManage = canPlatform(role, 'platform:jobs:manage');

  const [queues, setQueues] = useState<PlatformJobQueueList | null>(null);
  const [jobs, setJobs] = useState<PlatformFailedJob[]>([]);
  const [total, setTotal] = useState(0);
  const [state, setState] = useState<LoadState>('loading');
  const [errorMessage, setErrorMessage] = useState('');
  const [queueFilter, setQueueFilter] = useState('');
  const [pageSize, setPageSize] = useState<number>(PAGE_SIZES[0]);
  const [offset, setOffset] = useState(0);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [retryingId, setRetryingId] = useState<string | null>(null);
  const [notice, setNotice] = useState('');
  const [reloadToken, setReloadToken] = useState(0);

  const reload = useCallback(() => setReloadToken((t) => t + 1), []);

  useEffect(() => {
    const controller = new AbortController();
    setState('loading');
    setExpandedId(null);

    Promise.all([
      fetchJobQueues(controller.signal),
      fetchFailedJobs(
        { limit: pageSize, offset, ...(queueFilter ? { queue: queueFilter } : {}) },
        controller.signal,
      ),
    ])
      .then(([queueList, page]) => {
        if (controller.signal.aborted) return;
        setQueues(queueList);
        setJobs(page.items);
        setTotal(page.total);
        setState('ready');
      })
      .catch((error: unknown) => {
        if (controller.signal.aborted) return;
        // An outage must not render as "no failed jobs" — that would read as
        // healthy. Same honesty rule the audit-log page follows.
        setErrorMessage(getApiErrorMessage(error, 'Unable to load background jobs.'));
        setQueues(null);
        setJobs([]);
        setTotal(0);
        setState('error');
      });

    return () => controller.abort();
  }, [pageSize, offset, queueFilter, reloadToken]);

  const rangeLabel = useMemo(() => {
    if (total === 0 || jobs.length === 0) return null;
    return `Showing ${(offset + 1).toLocaleString()}–${(offset + jobs.length).toLocaleString()} of ${total.toLocaleString()}`;
  }, [offset, jobs.length, total]);

  const onRetry = async (job: PlatformFailedJob) => {
    const key = `${job.queue}:${job.id}`;
    setRetryingId(key);
    setNotice('');
    try {
      await retryJob(job.queue, job.id);
      setNotice(`Job ${job.id} on ${job.queue} was re-queued.`);
      reload();
    } catch (error: unknown) {
      setNotice(getApiErrorMessage(error, `Could not retry job ${job.id}.`));
    } finally {
      setRetryingId(null);
    }
  };

  const canPrev = offset > 0;
  const canNext = offset + jobs.length < total;

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold text-foreground">Background Jobs</h1>
          <p className="mt-0.5 text-sm text-muted-foreground">
            Live queue state and recently failed jobs.
          </p>
        </div>
        <button
          type="button"
          onClick={reload}
          disabled={state === 'loading'}
          aria-label="Refresh background jobs"
          className="inline-flex h-11 items-center gap-2 rounded-lg border border-border px-4 text-sm font-medium text-foreground transition-colors hover:bg-muted disabled:cursor-not-allowed disabled:opacity-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
        >
          <FontAwesomeIcon icon={faArrowsRotate} className="h-3.5 w-3.5" />
          Refresh
        </button>
      </div>

      {/* ── Queue inventory ─────────────────────────────────────────────── */}
      <section className="rounded-xl border border-border bg-card">
        <div className="border-b border-border px-5 py-3">
          <h2 className="text-sm font-semibold text-foreground">Queues</h2>
        </div>
        <div className="p-5">
          {state === 'loading' ? (
            <div data-testid="queues-loading">
              <SkeletonTable rows={5} cols={6} />
            </div>
          ) : state === 'error' ? (
            <p className="text-sm text-muted-foreground" data-testid="queues-error">
              {errorMessage}
            </p>
          ) : (
            <ul className="grid grid-cols-1 gap-3" data-testid="queue-list">
              {(queues?.queues ?? []).map((q) => (
                <li
                  key={q.name}
                  className="flex min-w-0 flex-col gap-2 rounded-lg border border-border p-4 sm:flex-row sm:items-center sm:justify-between"
                  data-testid={`queue-${q.name}`}
                >
                  <div className="min-w-0">
                    <span className="break-words text-sm font-medium text-foreground">
                      {q.name}
                    </span>
                    {/* A queue with no consumer is NOT idle. Saying "0 waiting"
                        without this would actively mislead an operator. */}
                    {!q.hasProcessor && (
                      <span
                        className="ml-2 rounded-full border border-border px-2 py-0.5 text-[11px] text-muted-foreground"
                        data-testid={`no-processor-${q.name}`}
                      >
                        No processor — jobs here will not run
                      </span>
                    )}
                  </div>
                  <dl className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground">
                    <Count label="Waiting" value={q.waiting} />
                    <Count label="Active" value={q.active} />
                    <Count label="Delayed" value={q.delayed} />
                    <Count label="Completed" value={q.completed} />
                    <Count label="Failed" value={q.failed} />
                    <Count label="Paused" value={q.paused} />
                  </dl>
                </li>
              ))}
            </ul>
          )}
        </div>
      </section>

      {/* ── Failed jobs ─────────────────────────────────────────────────── */}
      <section className="rounded-xl border border-border bg-card">
        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-border px-5 py-3">
          <p className="text-sm text-muted-foreground" aria-live="polite">
            {state === 'loading' ? 'Loading failed jobs…' : (rangeLabel ?? 'No failed jobs')}
          </p>
          <div className="flex flex-wrap items-center gap-2 text-sm text-muted-foreground">
            <label htmlFor="job-queue-filter">Queue</label>
            <select
              id="job-queue-filter"
              aria-label="Filter failed jobs by queue"
              value={queueFilter}
              onChange={(e) => {
                setQueueFilter(e.target.value);
                setOffset(0);
              }}
              className="h-11 min-w-0 rounded-lg border border-border bg-background px-2 text-sm text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
            >
              <option value="">All queues</option>
              {(queues?.queues ?? []).map((q) => (
                <option key={q.name} value={q.name}>
                  {q.name}
                </option>
              ))}
            </select>

            <label htmlFor="job-page-size">Per page</label>
            <select
              id="job-page-size"
              aria-label="Failed jobs per page"
              value={pageSize}
              onChange={(e) => {
                setPageSize(Number(e.target.value));
                setOffset(0);
              }}
              className="h-11 rounded-lg border border-border bg-background px-2 text-sm text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
            >
              {PAGE_SIZES.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </select>
          </div>
        </div>

        {notice !== '' && (
          <p className="border-b border-border px-5 py-2 text-sm text-foreground" role="status">
            {notice}
          </p>
        )}

        <div className="p-5">
          {state === 'loading' ? (
            <div data-testid="jobs-loading">
              <SkeletonTable rows={6} cols={5} />
            </div>
          ) : state === 'error' ? (
            <Message
              testId="jobs-error"
              message={errorMessage}
              onAction={reload}
              actionLabel="Retry"
            />
          ) : jobs.length === 0 ? (
            queueFilter !== '' ? (
              <Message
                testId="jobs-no-results"
                message={`No failed jobs on ${queueFilter}.`}
                onAction={() => setQueueFilter('')}
                actionLabel="Clear filter"
              />
            ) : (
              <Message testId="jobs-empty" message="No failed jobs in the retained window." />
            )
          ) : (
            <JobResults
              jobs={jobs}
              expandedId={expandedId}
              onToggle={(k) => setExpandedId((cur) => (cur === k ? null : k))}
              canManage={canManage}
              retryingId={retryingId}
              onRetry={onRetry}
            />
          )}
        </div>

        <div className="flex items-center justify-end gap-2 border-t border-border px-5 py-3">
          <button
            type="button"
            onClick={() => setOffset((o) => Math.max(0, o - pageSize))}
            disabled={!canPrev || state === 'loading'}
            className="inline-flex h-11 items-center rounded-lg border border-border px-4 text-sm font-medium text-foreground transition-colors hover:bg-muted disabled:cursor-not-allowed disabled:opacity-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
          >
            Previous
          </button>
          <button
            type="button"
            onClick={() => setOffset((o) => o + pageSize)}
            disabled={!canNext || state === 'loading'}
            className="inline-flex h-11 items-center rounded-lg border border-border px-4 text-sm font-medium text-foreground transition-colors hover:bg-muted disabled:cursor-not-allowed disabled:opacity-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
          >
            Next
          </button>
        </div>
      </section>

      {/* ── The rolling-window limit, stated rather than implied ─────────── */}
      <p className="text-xs text-muted-foreground" data-testid="jobs-retention-note">
        This is a rolling window, not durable history. The queue system retains the last{' '}
        {queues?.retention.completed ?? 0} completed and {queues?.retention.failed ?? 0} failed jobs
        per queue; anything older is no longer available, and job payloads are never shown.
      </p>
    </div>
  );
}

function Count({ label, value }: { label: string; value: number }) {
  return (
    <div className="flex items-center gap-1">
      <dt>{label}</dt>
      <dd className="font-medium text-foreground">{value}</dd>
    </div>
  );
}

function Message({
  message,
  onAction,
  actionLabel,
  testId,
}: {
  message: string;
  onAction?: () => void;
  actionLabel?: string;
  testId: string;
}) {
  return (
    <div
      className="flex flex-col items-center justify-center px-4 py-12 text-center"
      data-testid={testId}
      role="status"
    >
      <div className="flex h-11 w-11 items-center justify-center rounded-full bg-muted">
        <FontAwesomeIcon
          icon={testId === 'jobs-error' ? faTriangleExclamation : faGears}
          className="h-4 w-4 text-muted-foreground"
        />
      </div>
      <p className="mt-3 max-w-sm text-sm text-muted-foreground">{message}</p>
      {onAction && (
        <button
          type="button"
          onClick={onAction}
          className="mt-4 inline-flex h-11 items-center rounded-lg border border-border px-4 text-sm font-medium text-foreground transition-colors hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
        >
          {actionLabel ?? 'Retry'}
        </button>
      )}
    </div>
  );
}

function JobResults({
  jobs,
  expandedId,
  onToggle,
  canManage,
  retryingId,
  onRetry,
}: {
  jobs: PlatformFailedJob[];
  expandedId: string | null;
  onToggle: (key: string) => void;
  canManage: boolean;
  retryingId: string | null;
  onRetry: (job: PlatformFailedJob) => void;
}) {
  const when = (ms?: number): string => (ms === undefined ? '—' : new Date(ms).toLocaleString());

  return (
    <>
      {/* Desktop */}
      <div className="hidden md:block">
        <table className="w-full text-left text-sm">
          <caption className="sr-only">Failed background jobs, newest first</caption>
          <thead>
            <tr className="border-b border-border text-xs uppercase tracking-wide text-muted-foreground">
              <th scope="col" className="w-8 px-2 py-2" />
              <th scope="col" className="px-2 py-2 font-medium">
                Queue
              </th>
              <th scope="col" className="px-2 py-2 font-medium">
                Job
              </th>
              <th scope="col" className="px-2 py-2 font-medium">
                Attempts
              </th>
              <th scope="col" className="px-2 py-2 font-medium">
                Failed At
              </th>
              <th scope="col" className="px-2 py-2 font-medium">
                Reason
              </th>
              <th scope="col" className="px-2 py-2 font-medium">
                Action
              </th>
            </tr>
          </thead>
          <tbody>
            {jobs.map((job) => {
              const key = `${job.queue}:${job.id}`;
              const expanded = expandedId === key;
              return (
                <Fragment key={key}>
                  <tr className="border-b border-border last:border-0">
                    <td className="px-2 py-3 align-top">
                      <button
                        type="button"
                        onClick={() => onToggle(key)}
                        aria-expanded={expanded}
                        aria-controls={`job-detail-${key}`}
                        aria-label={`${expanded ? 'Hide' : 'Show'} details for job ${job.id}`}
                        data-testid={`job-expand-${key}`}
                        className="flex h-11 w-11 items-center justify-center rounded-lg text-muted-foreground transition-colors hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
                      >
                        <FontAwesomeIcon
                          icon={expanded ? faChevronDown : faChevronRight}
                          className="h-3 w-3"
                        />
                      </button>
                    </td>
                    <td className="px-2 py-3 align-top text-foreground">{job.queue}</td>
                    <td className="px-2 py-3 align-top font-medium text-foreground">
                      {job.name}
                      <span className="ml-1 text-xs text-muted-foreground">#{job.id}</span>
                    </td>
                    <td className="px-2 py-3 align-top text-muted-foreground">
                      {job.attemptsMade}
                      {job.maxAttempts === undefined ? '' : ` / ${job.maxAttempts}`}
                    </td>
                    <td className="whitespace-nowrap px-2 py-3 align-top text-muted-foreground">
                      {when(job.finishedOn)}
                    </td>
                    <td className="max-w-[18rem] truncate px-2 py-3 align-top text-muted-foreground">
                      {job.failedReason ?? '—'}
                    </td>
                    <td className="px-2 py-3 align-top">
                      <RetryControl
                        job={job}
                        canManage={canManage}
                        retrying={retryingId === key}
                        onRetry={onRetry}
                      />
                    </td>
                  </tr>
                  {expanded && (
                    <tr className="border-b border-border last:border-0">
                      <td colSpan={7} className="px-2 pb-4">
                        <JobDetail job={job} idKey={key} />
                      </td>
                    </tr>
                  )}
                </Fragment>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Narrow — stacked cards */}
      <ul className="grid grid-cols-1 gap-3 md:hidden" data-testid="job-cards">
        {jobs.map((job) => {
          const key = `${job.queue}:${job.id}`;
          const expanded = expandedId === key;
          return (
            <li key={key} className="min-w-0 rounded-lg border border-border p-4">
              <button
                type="button"
                onClick={() => onToggle(key)}
                aria-expanded={expanded}
                aria-controls={`job-detail-card-${key}`}
                className="flex w-full min-w-0 items-start justify-between gap-3 text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
              >
                <span className="min-w-0">
                  <span className="block break-words text-sm font-semibold text-foreground">
                    {job.name} <span className="text-xs font-normal">#{job.id}</span>
                  </span>
                  <span className="mt-0.5 block text-xs text-muted-foreground">
                    {job.queue} · {when(job.finishedOn)}
                  </span>
                </span>
                <FontAwesomeIcon
                  icon={expanded ? faChevronDown : faChevronRight}
                  className="mt-1 h-3 w-3 shrink-0 text-muted-foreground"
                />
              </button>
              <dl className="mt-3 grid grid-cols-1 gap-1.5 text-xs">
                <Field
                  label="Attempts"
                  value={`${job.attemptsMade}${job.maxAttempts === undefined ? '' : ` / ${job.maxAttempts}`}`}
                />
                <Field label="Reason" value={job.failedReason ?? '—'} />
              </dl>
              <div className="mt-3">
                <RetryControl
                  job={job}
                  canManage={canManage}
                  retrying={retryingId === key}
                  onRetry={onRetry}
                />
              </div>
              {expanded && (
                <div className="mt-3" id={`job-detail-card-${key}`}>
                  <JobDetail job={job} idKey={key} />
                </div>
              )}
            </li>
          );
        })}
      </ul>
    </>
  );
}

/**
 * Retry is hidden without `platform:jobs:manage`, and absent entirely for a
 * queue with no processor — retrying there would move the job out of `failed`
 * into a queue nothing consumes, so it would silently never run.
 */
function RetryControl({
  job,
  canManage,
  retrying,
  onRetry,
}: {
  job: PlatformFailedJob;
  canManage: boolean;
  retrying: boolean;
  onRetry: (job: PlatformFailedJob) => void;
}) {
  if (!canManage) return <span className="text-xs text-muted-foreground">—</span>;
  if (!job.retryable) {
    return (
      <span
        className="text-xs text-muted-foreground"
        data-testid={`retry-unavailable-${job.queue}:${job.id}`}
      >
        No processor
      </span>
    );
  }
  return (
    <button
      type="button"
      onClick={() => onRetry(job)}
      disabled={retrying}
      data-testid={`retry-${job.queue}:${job.id}`}
      className="inline-flex h-11 items-center rounded-lg border border-border px-3 text-xs font-medium text-foreground transition-colors hover:bg-muted disabled:cursor-not-allowed disabled:opacity-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
    >
      {retrying ? 'Retrying…' : 'Retry'}
    </button>
  );
}

/** Operational metadata only — the payload is never returned and never shown. */
function JobDetail({ job, idKey }: { job: PlatformFailedJob; idKey: string }) {
  const when = (ms?: number): string => (ms === undefined ? '—' : new Date(ms).toISOString());
  return (
    <div
      id={`job-detail-${idKey}`}
      className="rounded-lg bg-muted/40 p-4"
      data-testid={`job-detail-${idKey}`}
    >
      <dl className="grid grid-cols-1 gap-2 text-xs sm:grid-cols-2">
        <Field label="Queue" value={job.queue} />
        <Field label="Job ID" value={job.id} mono />
        <Field label="Job name" value={job.name} />
        <Field label="Created" value={when(job.timestamp)} mono />
        <Field label="Started" value={when(job.processedOn)} mono />
        <Field label="Failed" value={when(job.finishedOn)} mono />
        <div className="min-w-0 sm:col-span-2">
          <dt className="text-muted-foreground">Failure reason</dt>
          <dd className="break-words font-mono text-[11px] text-foreground">
            {job.failedReason ?? '—'}
          </dd>
        </div>
      </dl>
    </div>
  );
}

function Field({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
  return (
    <div className="min-w-0">
      <dt className="text-muted-foreground">{label}</dt>
      <dd className={`break-words text-foreground ${mono ? 'font-mono text-[11px]' : ''}`}>
        {value}
      </dd>
    </div>
  );
}
